/**
 * Horizontal row of 8 capability pills rendered above the Custom KQL
 * editor. Each pill reflects one flag from the tenant's persisted
 * dbo.CustomerCapabilities snapshot, with a tooltip explaining which
 * Defender Advanced Hunting tables the capability gates.
 *
 * Purpose: let the analyst see at a glance what the tenant is and isn't
 * licensed for, so they don't burn 14 seconds on a query that will come
 * back empty for license reasons. Paired with the empty-state hint
 * surfaced in KqlEditor when 0 rows return.
 *
 * Renders nothing if the snapshot is unavailable (fail-open) — the strip
 * is informational, not blocking.
 */
import type { HuntingCapability } from '../../data/types';
import { capabilityLabel } from '../../lib/huntingTableCapabilities';

interface CapabilityStripProps {
  capabilities: Partial<Record<HuntingCapability, boolean>> | null;
  lastRefreshedAt: string | null;
}

const STRIP_ORDER: ReadonlyArray<HuntingCapability> = [
  'mde',
  'mdo',
  'mdca',
  'mdi',
  'entraP1',
  'entraP2',
  'purviewIrm',
  'defenderXdrBaseline',
];

const TOOLTIP: Record<HuntingCapability, string> = {
  mde: 'Gates Device* tables (DeviceProcessEvents, DeviceNetworkEvents, DeviceLogonEvents, DeviceInfo, etc.)',
  mdo: 'Gates Email* + UrlClickEvents tables',
  mdca: 'Gates CloudAppEvents. Discovery-only tenants pass the gate but see only shadow-IT events, not MCAS policy data.',
  mdi: 'Gates Identity* tables (IdentityLogonEvents, IdentityDirectoryEvents, etc.)',
  entraP1: 'Conditional access reporting (no Advanced Hunting table)',
  entraP2: 'Gates AADSignInEventsBeta risk-level columns',
  purviewIrm: 'Reserved for future Insider Risk hunting tables',
  defenderXdrBaseline: 'Gates AlertInfo + AlertEvidence (any Defender XDR licence grants this)',
};

function formatRefreshedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const now = Date.now();
  const ageMs = now - date.getTime();
  const hours = Math.floor(ageMs / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function CapabilityStrip({ capabilities, lastRefreshedAt }: CapabilityStripProps) {
  if (!capabilities) return null;

  return (
    <div className="mx-4 mb-3 p-3 bg-bg-tertiary/40 border border-border-default rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">
          Tenant capabilities
        </span>
        {lastRefreshedAt && (
          <span className="text-[11px] text-text-secondary">
            refreshed {formatRefreshedAt(lastRefreshedAt)}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {STRIP_ORDER.map((cap) => {
          const on = !!capabilities[cap];
          return (
            <span
              key={cap}
              title={TOOLTIP[cap]}
              className={
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ' +
                (on
                  ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-700/40 dark:bg-green-900/20 dark:text-green-300'
                  : 'border-border-default bg-bg-tertiary/40 text-text-secondary')
              }
            >
              <span className="font-mono text-[9px] uppercase">{on ? 'on' : 'off'}</span>
              {capabilityLabel(cap)}
            </span>
          );
        })}
      </div>
    </div>
  );
}
